/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3, ChevronDown, ChevronUp } from "lucide-react";

const COLORS = { la: "#e11d48", norcal: "#2563eb", sd: "#f59e0b", other: "#94a3b8" };
const LS_KEY = "crm.weeklyNew.collapsed";

/* 自定义刻度渲染（性能，2026-10-20 客户线索看板卡顿需求）
 *
 * 为什么不用 Recharts 默认刻度：
 * Recharts 的默认刻度是 <Text> 组件，CartesianAxis 会把坐标轴的 width 作为 SVG 属性透传给每个
 * 刻度；Text 一旦拿到 width 就会走「按词测量换行」分支，对每个刻度调用 getStringSize() →
 * 往 body 插入隐藏 span → getBoundingClientRect()。每次测量都会让整页样式重算一遍，
 * 本页约 1 万节点、实测 8ms/次，首屏 20 多次测量合计 200ms+，是最后一个 >200ms 长任务的来源。
 *
 * 这里改为直接输出 <text>：坐标、字号、颜色、对齐方式与默认刻度完全一致
 * （X 轴 dy=0.71em 对应默认 capHeight，Y 轴 dy=0.355em 对应默认垂直居中），
 * 只是不再做换行测量 —— 刻度文案都是短数字/日期，本来也不会换行，视觉无差异。 */
const AxisTickBase = ({ x, y, payload, fill, fontSize, textAnchor, verticalAnchor, className }) => (
  <text x={x} y={y} dy={verticalAnchor === "middle" ? "0.355em" : "0.71em"} textAnchor={textAnchor} fill={fill} fontSize={fontSize} className={className}>
    {payload == null ? "" : payload.value}
  </text>
);
/* 两条轴各自固定字号/颜色，和改造前 tick={{ fontSize: 10, fill: ... }} 完全一致 */
const XTick = (props) => <AxisTickBase {...props} fontSize={10} fill="#64748b" />;
const YTick = (props) => <AxisTickBase {...props} fontSize={10} fill="#94a3b8" />;

/* 延迟一帧挂载网格线（性能，2026-10-20 客户线索看板卡顿需求）
 *
 * 为什么：CartesianGrid 生成网格线坐标时走的是 Recharts store 里的坐标轴配置。图表首帧渲染时
 * 轴的设置还没注册进 store，网格会退回到默认 interval="preserveEnd"，于是又对每个分类刻度
 * 做一次 getStringSize() 文字测量（实测首屏残留 8 次、约 80ms）。等一帧后轴设置已就绪，
 * interval=0 生效，测量完全跳过。
 * 视觉等价：网格线只晚一帧出现（约 16ms），位置、颜色、粗细与原来一致。 */
const LazyGrid = (props) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(id);
  }, []);
  return ready ? <CartesianGrid {...props} /> : null;
};

/* 近 8 周每周新增客户 × 区域 堆叠柱（服务端聚合，随区域 / 销售筛选联动；可折叠，记忆到本浏览器） */
export function WeeklyNewChart({ api, zh, region, assignedSales, weeks = 8, activeStart, onPickWeek }) {
  const [d, setD] = useState(undefined);   // undefined = 加载中 · null = 失败（显示重试，不再静默消失）
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_KEY) === "1");
  useEffect(() => {
    let alive = true;
    setD(undefined); setErr("");
    api.get("/crm/lead-stats/weekly-new", { params: { weeks, ...(region ? { region } : {}), ...(assignedSales ? { assignedSales } : {}) } })
      .then(({ data }) => alive && setD(data))
      .catch((e) => { if (alive) { setD(null); setErr(e?.response?.status ? `HTTP ${e.response.status}` : (e?.code || e?.message || "network")); } });
    return () => { alive = false; };
  }, [api, region, assignedSales, weeks, tick]);
  const toggle = () => setCollapsed((v) => { localStorage.setItem(LS_KEY, v ? "0" : "1"); return !v; });
  if (d === undefined) return <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-400 inline-flex items-center gap-2" data-testid="crm-weekly-loading"><BarChart3 className="h-3.5 w-3.5 text-slate-300" />{zh ? "每周新增趋势加载中…" : "Loading weekly trend…"}</div>;
  if (!d) return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800 flex items-center gap-2 flex-wrap" data-testid="crm-weekly-error" data-err={err}>
      <BarChart3 className="h-3.5 w-3.5" />{zh ? `每周新增趋势图加载失败（${err}）` : `Weekly trend failed to load (${err})`}
      <button type="button" onClick={() => setTick((v) => v + 1)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto underline font-semibold" data-testid="crm-weekly-retry">{zh ? "重试" : "Retry"}</button>
    </div>
  );
  const regions = d.regions || [];
  const rows = d.weeks.map((w) => ({ label: w.label, start: w.start, total: w.total, ...w.byRegion, other: w.other }));
  const hasOther = rows.some((r) => r.other > 0);
  const pick = (entry) => { const s = entry?.start || entry?.payload?.start; if (s && onPickWeek) onPickWeek(s); };
  const cells = (key) => rows.map((r) => <Cell key={`${key}-${r.start}`} fillOpacity={activeStart && r.start !== activeStart ? 0.3 : 1} cursor={onPickWeek ? "pointer" : "default"} />);
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="crm-weekly-new" data-collapsed={collapsed ? 1 : 0} data-total={d.total} data-active={activeStart || ""}>
      <button type="button" onClick={toggle} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full flex items-center gap-2 px-3 py-2 text-xs" data-testid="crm-weekly-toggle">
        <BarChart3 className="h-3.5 w-3.5 text-brand-600" />
        <span className="font-semibold text-slate-700">{zh ? `近 ${d.weeks.length} 周每周新增客户` : `New leads per week (last ${d.weeks.length})`}</span>
        <span className="text-slate-500" data-testid="crm-weekly-summary">{zh ? `合计 ${d.total} · 本周 ${d.thisWeek}` : `Total ${d.total} · this week ${d.thisWeek}`}{region ? ` · ${(regions.find((r) => r.id === region) || {})[zh ? "zh" : "en"] || region}` : ""}{assignedSales ? ` · ${assignedSales}` : ""}</span>
        {activeStart && <span className="text-[10.5px] text-brand-700 bg-brand-50 border border-brand-200 rounded-full px-2 py-0.5" data-testid="crm-weekly-active">{zh ? `已下钻：周一 ${activeStart} 起 · 再点同一柱取消` : `Drilled: week of ${activeStart}`}</span>}
        {!activeStart && onPickWeek && <span className="text-[10.5px] text-slate-400 hidden md:inline">{zh ? "点柱子 → 列表只看那一周新增" : "Click a bar to drill down"}</span>}
        <span className="ml-auto text-slate-400 inline-flex items-center gap-1">{zh ? (collapsed ? "展开" : "收起") : (collapsed ? "Show" : "Hide")}{collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}</span>
      </button>
      {!collapsed && (
        <div className="px-2 pb-2 h-[150px]" data-testid="crm-weekly-body">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }} barCategoryGap="28%" onClick={(st) => st?.activePayload?.[0]?.payload && pick(st.activePayload[0].payload)}>
              {/* 性能（2026-10-20 客户线索看板卡顿需求）：XAxis/YAxis 显式给数字 interval={0}。
                  Recharts 默认 interval 是 "preserveEnd"，会为每个刻度调用 getStringSize() →
                  往 body 里塞一个隐藏 span 再 getBoundingClientRect()，每次都会触发整页样式重算
                  （实测本页 8ms/次 × 34 次 ≈ 270ms，首屏单个 400ms+ 长任务就来自这里）。
                  传数字 interval 时 Recharts 走 getNumberIntervalTicks 分支，完全跳过文字测量。
                  视觉等价：8 个周刻度、5 个数量刻度在当前宽度下本来就是全部显示，不裁任何标签；
                  刻度文案、字号、颜色、点击下钻逻辑均不变。 */}
              <XAxis dataKey="label" tick={<XTick />} axisLine={false} tickLine={false} interval={0} />
              <YAxis allowDecimals={false} tick={<YTick />} axisLine={false} tickLine={false} interval={0} />
              <LazyGrid vertical={false} stroke="#f1f5f9" />
              <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v, name) => [v, name]} labelFormatter={(l) => (zh ? `周一 ${l} 起` : `Week of ${l}`)} />
              <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
              {regions.map((r) => <Bar key={r.id} dataKey={r.id} name={zh ? r.zh : r.en} stackId="w" fill={COLORS[r.id] || "#64748b"} radius={0} isAnimationActive={false} onClick={pick}>{cells(r.id)}</Bar>)}
              {hasOther && <Bar dataKey="other" name={zh ? "未分区域" : "No region"} stackId="w" fill={COLORS.other} isAnimationActive={false} onClick={pick}>{cells("other")}</Bar>}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
